import React, { useState, useEffect, useRef } from "react";
import {
  Typography,
  Button,
  Modal,
  Box,
  TextField,
  Stack,
  Alert,
  ButtonGroup,
  useTheme,
  Link,
} from "@mui/material";
import { EditPriceAlertApiArg, useEditPriceAlertMutation } from "src/store/features/api/generated";
import { trackEvent } from "src/Amplitude";
import Preloader from "src/utils/Preloader";
import { CoinPerformanceStyles } from "src/components/coins/styles/CoinPerformance.styles";
import {
  togglePriceAlertEditModal,
  usePriceAlertState,
} from "src/store/features/PriceAlerts.slice";
import { useAppDispatch } from "src/store/store";
import { toggleFilterOptions } from "src/store/features/markets.slice";
import CloseIcon from "@mui/icons-material/Close";
import formatCoinName from "src/utils/formatCoinName";
import NumberFormatCustom from "src/components/trade/NumberField";
import { formatCurrency } from "src/utils/helpers/PriceAndUnitsHelpers";

interface PriceAlertsProps {
  refetchData: React.Dispatch<React.SetStateAction<boolean>>;
  price?: number;
  currencyConfig?: any;
  subtitle?: any;
  logoUrl?: any;
  crossOverPrice?: number;
  id?: string;
}

const PriceAlertEditModel = (props: PriceAlertsProps) => {
  // ------------- REDUX---------------------
  const [postPriceAlert, { reset, isSuccess, isError, error }] = useEditPriceAlertMutation();
  const { displayEditPriceAlert } = usePriceAlertState();
  const dispatchRedux = useAppDispatch();

  const {
    price = 0,
    currencyConfig,
    subtitle,
    logoUrl,
    refetchData,
    crossOverPrice = 0,
    id = "",
  } = props;
  const classes = CoinPerformanceStyles();
  const theme = useTheme();
  const style = {
    position: "absolute" as "absolute",
    top: "50%",
    left: "50%",
    borderRadius: "10px",
    transform: "translate(-50%, -50%)",
    width: 400,
    bgcolor: "background.paper",
    boxShadow: 24,
    [theme.breakpoints.down("md")]: {
      width: 350,
    },
  };
  // ------------- CONSTS---------------------
  const MODEL_TITLE = "Edit Price Alert";
  const MODEL_SUBTITLE =
    "Receive a notification when the price crosses the target price. Read more about price alerts here";
  const INVALID_WARNING = "Price alert is set as current market price. Please set a new price";
  const RANGE_ERROR = "Please set the price alert with a deviation greater than +/- 0.5%";
  // -------------PRICE ALERT STATE----------------------
  const [invalidTarget, setInvalidTarget] = useState(false);
  const [priceAlertLoading, setPriceAlertLoading] = useState(false);
  const [formattedPrice, setFormattedPrice] = useState(price);
  const [validTarget, setValidTarget] = useState("RED");
  const [isRange, setIsRange] = useState(true);
  const inputRef = useRef<HTMLDivElement | null>(null);

  const targetPriceHandler = (event: any) => {
    formatPrice(event.target.value);
  };

  // Make sure it is not withing +/- 0.5%
  const checkPriceLimit = (price: number, target: number) => {
    const lower = target * 0.995;
    const upper = target * 1.005;
    return price >= lower && price <= upper;
  };

  const submitTargetOrderEdit = (price: number, code: string, id: string) => {
    const reqBody: EditPriceAlertApiArg = {
      existingPriceAlertDto: { crossOverPrice: price, code, id },
    };
    setPriceAlertLoading(true);
    postPriceAlert(reqBody);

    if (isSuccess) {
      setPriceAlertLoading(false);
      trackEvent(`Submit_fulfilled`, { token: currencyConfig.code }, "Price_Alert");
    }
    if (isError) {
      setPriceAlertLoading(false);
      trackEvent(`Submit_rejected`, { token: currencyConfig.code }, "Price_Alert");
    }
  };
  const formatPrice = (price: number) => {
    if (price < 1) {
      setFormattedPrice(price);
    } else if (price >= 1) {
      setFormattedPrice(Math.round(price * 100) / 100);
    } else {
      return setFormattedPrice(price);
    }
  };
  const percentageHandler = (value: number, price: number) => {
    const formattedPercentageNumber = price + price * value;
    formatPrice(formattedPercentageNumber);
  };

  const validatePriceAlert = (inputValue: number, currentPrice: number) => {
    const formatted = currentPrice > 1 ? Math.round(currentPrice * 100) / 100 : currentPrice;
    if (inputValue === formatted) {
      setValidTarget(theme.palette.error.main);
      setInvalidTarget(true);
    } else {
      setValidTarget(theme.palette.primary.main);
      setInvalidTarget(false);
    }
  };
  // SELECTS ALL INPUT TEXT
  const handleBoxClick = () => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };
  // -------------EFFECTS----------------------
  const handleClose = () => {
    dispatchRedux(togglePriceAlertEditModal());
    setPriceAlertLoading(false);
    formatPrice(price);
    refetchData(true);
    reset();
  };

  useEffect(() => {
    validatePriceAlert(formattedPrice, price);
    const isRange = checkPriceLimit(price, formattedPrice);
    setIsRange(isRange);
  }, [formattedPrice]);
  // set base price.
  useEffect(() => {
    formatPrice(price);
  }, [price]);
  return (
    <Modal
      open={displayEditPriceAlert}
      onClose={handleClose}
      aria-labelledby="parent-modal-price-alert"
      aria-describedby="parent-modal-price-alert">
      <Box sx={style}>
        <Stack spacing={2}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            spacing={2}
            sx={{
              width: "100%",
              borderTopLeftRadius: "10px",
              borderTopRightRadius: "10px",
              bgcolor: theme.palette.primary.main,
              padding: "20px 25px 15px 25px",
            }}>
            <img src={logoUrl} alt={`${subtitle} logo`} className={classes.logoImage} />
            <Typography id="cardTitle" sx={{ fontWeight: 900, color: "#fff" }}>
              <span id="priceModelTitle" style={{ fontWeight: 900, color: "#fff" }}>
                {currencyConfig?.name && formatCoinName(currencyConfig?.name)}
              </span>
              {` ${MODEL_TITLE}`}
            </Typography>
            <CloseIcon sx={{ cursor: "pointer", color: "#fff" }} onClick={handleClose} />
          </Stack>
          <Typography sx={{ padding: "10px 25px 0px 25px" }} id="explainer">
            {MODEL_SUBTITLE}
          </Typography>
        </Stack>

        {!isSuccess && !isError && (
          <Stack sx={{ mt: 2, padding: "10px 25px 15px 25px" }} spacing={2}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
              <Typography>My Price Alert</Typography>
              <Typography id="crossover" sx={{ fontWeight: 900 }}>
                {formatCurrency(crossOverPrice, "usd")}
              </Typography>
            </Stack>

            <TextField
              id="outlined-number"
              onClick={handleBoxClick}
              label="Alert Price"
              variant="outlined"
              inputRef={inputRef}
              onFocus={(event: any) => event.target.select()} // select all
              InputProps={{
                inputComponent: NumberFormatCustom as any,
                inputProps: { min: 0 },
              }}
              sx={{
                input: {
                  color: validTarget,
                },
              }}
              onChange={targetPriceHandler}
              value={formattedPrice}
              InputLabelProps={{
                shrink: true,
              }}
            />
            {priceAlertLoading && <Preloader />}
            {!priceAlertLoading && (
              <>
                <ButtonGroup variant="outlined" aria-label="outlined button group">
                  <Button disableRipple onClick={() => percentageHandler(-0.3, price)}>
                    -30%
                  </Button>
                  <Button disableRipple onClick={() => percentageHandler(-0.2, price)}>
                    -20%
                  </Button>
                  <Button disableRipple onClick={() => percentageHandler(-0.1, price)}>
                    -10%
                  </Button>
                  <Button disableRipple onClick={() => percentageHandler(0.1, price)}>
                    10%
                  </Button>
                  <Button disableRipple onClick={() => percentageHandler(0.2, price)}>
                    20%
                  </Button>
                  <Button disableRipple onClick={() => percentageHandler(0.3, price)}>
                    30%
                  </Button>
                </ButtonGroup>
                {isRange && !invalidTarget && <Alert severity="error">{RANGE_ERROR}</Alert>}
                {invalidTarget && (
                  <Alert sx={{ mt: 3, mb: 3, borderRadius: "10px" }} severity="info">
                    {INVALID_WARNING}
                  </Alert>
                )}
                <Button
                  disableRipple
                  onClick={() => {
                    trackEvent(`Submit_Price_Alert_Edit`, {
                      Submit_Price_Alert_Edit: currencyConfig?.code,
                    });
                    submitTargetOrderEdit(formattedPrice, currencyConfig.code, id);
                  }}
                  disabled={invalidTarget}
                  variant="contained"
                  sx={{ mt: 2 }}>
                  {invalidTarget ? "Disabled" : "Submit"}
                </Button>
              </>
            )}
          </Stack>
        )}
        {isError && (
          <Alert sx={{ mt: 5, margin: "10px 25px 15px 25px" }} severity="error">
            {error?.message}
          </Alert>
        )}

        {isSuccess && (
          <Alert
            sx={{
              mt: 5,
              cursor: "pointer",
              margin: "10px 25px 15px 25px",
            }}
            severity="success">
            Price Alert Placed — check it out
            <Link
              sx={{ marginLeft: "3px" }}
              onClick={() => {
                refetchData(true);
                dispatchRedux(toggleFilterOptions("Alerts"));
                dispatchRedux(togglePriceAlertEditModal());
              }}>
              here
            </Link>
            !
          </Alert>
        )}
      </Box>
    </Modal>
  );
};

export default PriceAlertEditModel;
